import { DATABASE_LARGE_DATA_POLICY } from '@streamlain/database-identity';
import Papa from 'papaparse';

import { ImportLimitError } from './import-limit-error';

const READ_BYTES = 64 * 1024;
// A record must fit into one row; cell limits are checked per column type.
const ROW_BYTE_LIMIT = DATABASE_LARGE_DATA_POLICY.row.encodedByteHardLimit;
const CSV_LINEBREAKS = ['\r\n', '\n', '\r'] as const;

function csvLinebreak(value: string): (typeof CSV_LINEBREAKS)[number] {
  const linebreak = CSV_LINEBREAKS.find(candidate => candidate === value);
  if (!linebreak) throw new Error('CSV line break could not be determined.');
  return linebreak;
}

/** Same bounded file reader for preview and import; consumers control demand. */
export async function* streamCsvRows(
  file: File,
  options: { signal?: AbortSignal; readBytes?: number; delimiter?: string } = {}
): AsyncGenerator<string[]> {
  const readBytes = options.readBytes ?? READ_BYTES;
  if (
    !Number.isSafeInteger(readBytes) ||
    readBytes <= 0 ||
    readBytes > READ_BYTES
  )
    throw new RangeError('CSV read chunk must be between 1 and 65536 bytes.');
  const decoder = new TextDecoder('utf-8', { fatal: true });
  const encoder = new TextEncoder();
  let pending = '';
  // Sniff a bounded prefix independently of the transport chunk boundaries.
  // A non-final prefix may end in the middle of a UTF-8 character.
  const prefixBytes = await file.slice(0, READ_BYTES).arrayBuffer();
  const prefix = new TextDecoder('utf-8', { fatal: true })
    .decode(prefixBytes, { stream: file.size > READ_BYTES })
    .replace(/^\uFEFF/, '');
  const inferred = Papa.parse<string[]>(prefix, {
    preview: 10,
    skipEmptyLines: true,
    delimiter: options.delimiter,
  });
  const parser = new Papa.Parser({
    delimiter: inferred.meta.delimiter,
    newline: csvLinebreak(inferred.meta.linebreak),
  });
  let first = true;
  const assertActive = () => {
    if (!options.signal?.aborted) return;
    const error = new Error('CSV import was cancelled.');
    error.name = 'AbortError';
    throw error;
  };
  // The low-level Papa parser is the same tokenizer used by Papa's chunk
  // streamers. Its cursor leaves an incomplete quoted record for the next
  // chunk. TextDecoder preserves UTF-8 characters across byte boundaries.
  for (let offset = 0; offset < file.size || pending; offset += readBytes) {
    assertActive();
    const end = Math.min(offset + readBytes, file.size);
    const finished = end === file.size;
    pending += decoder.decode(await file.slice(offset, end).arrayBuffer(), {
      stream: !finished,
    });
    assertActive();
    if (first) {
      pending = pending.replace(/^\uFEFF/, '');
      first = false;
    }
    const result = parser.parse(pending, 0, !finished) as Papa.ParseResult<
      string[]
    >;
    // Papa can report InvalidQuotes when a quoted record ends at the CR
    // half of CRLF. Its unconsumed final row is retried with the next chunk;
    // validate that row only once complete (or at EOF). Never ignore errors
    // in consumed rows, and retain the bounded pending-record limit below.
    const error = result.errors.find(
      error => finished || error.row == null || error.row < result.data.length
    );
    if (error)
      throw new Error(`Failed to parse CSV "${file.name}": ${error.message}`);
    for (const row of result.data) {
      assertActive();
      // Row/shard admission is independent of the total CSV file size.
      const recordBytes = encoder.encode(JSON.stringify(row)).byteLength;
      if (recordBytes > ROW_BYTE_LIMIT)
        throw new ImportLimitError({
          kind: 'record-bytes',
          actual: recordBytes,
          limit: ROW_BYTE_LIMIT,
        });
      if (row.length === 1 && row[0] === '') continue;
      yield row;
    }
    pending = pending.slice(result.meta.cursor);
    const pendingBytes = encoder.encode(pending).byteLength;
    if (pendingBytes > ROW_BYTE_LIMIT)
      throw new ImportLimitError({
        kind: 'record-bytes',
        actual: pendingBytes,
        limit: ROW_BYTE_LIMIT,
      });
    if (finished) break;
  }
}
